import { Link } from "react-router-dom";
import {
  ArrowRight, HeartHandshake, Sunrise, School, Video, Library,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { IMAGES } from "@/data/content";
import { useContent } from "@/context/ContentContext";

const ICONS = { HeartHandshake, Sunrise, School, Video, Library };

export default function Services() {
  const { services } = useContent();
  return (
    <>
      <section className="container-site pb-16 pt-16 sm:pt-24" data-testid="services-hero">
        <p className="overline-label">Services</p>
        <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.1}>Practical support,</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-terracotta-500">delivered with care.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Every engagement starts with listening and ends with your confidence. Explore
            the ways we coach and support caregivers — and the families behind them.
          </p>
        </Reveal>
      </section>

      <section className="container-site pb-24" data-testid="services-grid">
        <div className="grid gap-6 md:grid-cols-2">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || HeartHandshake;
            return (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link
                  to={s.href || `/services/${s.slug}`}
                  data-testid={`services-card-${s.slug}`}
                  className="card-lift group flex h-full flex-col rounded-3xl border border-border bg-card p-9 shadow-soft"
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/10 text-forest transition-colors duration-300 group-hover:bg-terracotta-500/15 group-hover:text-terracotta-500 dark:bg-sage/10 dark:text-sage">
                      <Icon className="h-6 w-6" />
                    </span>
                    <ArrowRight className="h-5 w-5 text-muted-foreground transition-all duration-300 group-hover:translate-x-1 group-hover:text-terracotta-500" />
                  </div>
                  <h2 className="mt-7 font-heading text-2xl font-semibold text-foreground">{s.title}</h2>
                  <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-terracotta-500">{s.tagline}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                  <p className="mt-6 text-sm font-medium text-forest transition-colors duration-300 group-hover:text-terracotta-500 dark:text-sage dark:group-hover:text-terracotta-400">
                    Overview, benefits &amp; FAQs →
                  </p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-y border-border bg-card py-24" data-testid="services-approach-section">
        <div className="container-site grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline-label">Not sure where to start?</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Start with a free discovery call.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                Fifteen minutes, no pressure, no jargon. We will listen to what is happening,
                answer your questions, and point you toward the right support — even if that
                support is not us. And as we grow — professional trainings, digital courses,
                memberships, and new tools — future offerings will slot right into the support
                you already know.
              </p>
              <Link to="/book" data-testid="services-discovery-button" className="btn-primary mt-8">
                Book a free discovery call <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <img
              src={IMAGES.consultation}
              alt="A calm professional consultation"
              className="h-72 w-full rounded-3xl object-cover shadow-lift dark:shadow-soft-dark"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
